import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../auth/usePermissions';
import { canAccessPageByPerm } from '../../lib/permissions';

/**
 * Tour de boas-vindas no PRIMEIRO acesso. Apresenta, passo a passo, os módulos
 * que o colaborador pode acessar — filtrados pela PERMISSÃO dele (mesma regra
 * do menu lateral). Mostrado uma vez por usuário (localStorage). O dono tem o
 * OnboardingWizard de configuração, então o tour é pros demais papéis.
 */

interface ModuleStep {
  to: string;
  title: string;
  icon: string;
  desc: string;
}

// Ordem e descrições dos módulos. Cada um só entra se o usuário tiver acesso.
const MODULES: ModuleStep[] = [
  { to: '/painel', icon: '🏠', title: 'Painel', desc: 'Sua visão geral do dia: agenda, pendências e números da clínica num relance.' },
  { to: '/agenda', icon: '📅', title: 'Agenda', desc: 'Marque, confirme e acompanhe as consultas. Arraste para remarcar e veja a semana inteira.' },
  { to: '/pacientes', icon: '🧑', title: 'Pacientes', desc: 'Cadastro completo, histórico e contato. O começo de quase tudo na clínica.' },
  { to: '/prontuario', icon: '🦷', title: 'Prontuário', desc: 'Registros clínicos, odontograma e avaliações — o histórico de atendimento do paciente.' },
  { to: '/planos', icon: '📋', title: 'Planos', desc: 'Monte planos de tratamento e pacotes, acompanhe o que foi aceito e concluído.' },
  { to: '/crm', icon: '🎯', title: 'Funil de vendas', desc: 'Acompanhe leads e oportunidades do primeiro contato ao fechamento.' },
  { to: '/atendimentos-ia', icon: '💬', title: 'Atendimentos IA', desc: 'Conversas do WhatsApp com apoio da secretária de IA — responda e assuma quando quiser.' },
  { to: '/faturas', icon: '🧾', title: 'Faturas', desc: 'Emita cobranças, registre pagamentos e acompanhe o que foi recebido.' },
  { to: '/contas-a-pagar', icon: '💸', title: 'Contas a pagar', desc: 'Lance as despesas da clínica, com pagamento parcial, recorrência e importação.' },
  { to: '/outras-receitas', icon: '💰', title: 'Outras receitas', desc: 'Entradas que não vêm de fatura (aporte, aluguel, reembolso) para o caixa fechar certo.' },
  { to: '/conciliacao', icon: '⚖️', title: 'Conciliação', desc: 'Importe o extrato do banco e concilie entradas e saídas com o que foi registrado.' },
  { to: '/relatorios', icon: '📊', title: 'Relatórios', desc: 'Resultado do mês, metas, fluxo de caixa e indicadores da clínica.' },
  { to: '/administracao', icon: '⚙️', title: 'Administração', desc: 'Serviços, equipe, integrações e configurações da clínica num só lugar.' },
];

const SEEN_PREFIX = 'dentalflow.welcome-tour.';

function seenKey(email?: string): string {
  return `${SEEN_PREFIX}${email ?? 'anon'}`;
}

export function WelcomeTour() {
  const { user } = useAuth();
  const { can, isLoading } = usePermissions();
  const navigate = useNavigate();

  const key = seenKey(user?.email);
  const [done, setDone] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const [step, setStep] = useState(0);

  // Módulos que ESTE usuário pode acessar (mesma regra do menu).
  const steps = useMemo(
    () => MODULES.filter((m) => canAccessPageByPerm(can, m.to)),
    [can],
  );

  // E2E: nunca abrir o tour (evita race de layout nos testes).
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  // O dono tem o wizard de configuração; não empilha o tour por cima.
  const skip =
    isE2E ||
    done ||
    isLoading ||
    user?.role === 'owner' ||
    user?.mustChangePassword ||
    steps.length === 0;

  if (skip) return null;

  const finish = (goTo?: string) => {
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* storage indisponível — fecha ao menos nesta sessão */
    }
    setDone(true);
    if (goTo) navigate(goTo);
  };

  // step 0 = intro; 1..N = módulos.
  const isIntro = step === 0;
  const mod = steps[step - 1];
  const total = steps.length + 1;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Tour de boas-vindas"
      >
        {isIntro ? (
          <div className="text-center">
            <div className="text-4xl">👋</div>
            <h2 className="mt-2 text-lg font-semibold text-slate-800">
              Boas-vindas ao Kevins Clínicas!
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Em poucos passos vamos te mostrar os módulos que você pode usar,
              de acordo com o seu acesso. É rapidinho.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-start gap-3">
              <span className="text-3xl" aria-hidden>
                {mod.icon}
              </span>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  {mod.title}
                </h2>
                <p className="mt-1 text-sm text-slate-600">{mod.desc}</p>
              </div>
            </div>
          </div>
        )}

        {/* Progresso */}
        <div className="mt-5 flex justify-center gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-5 bg-sky-600' : 'w-1.5 bg-slate-200'
              }`}
            />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => finish()}
            className="text-xs font-medium text-slate-400 hover:text-slate-600"
          >
            Pular tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Voltar
              </button>
            )}
            {step < total - 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                Próximo
              </button>
            ) : (
              <button
                type="button"
                onClick={() => finish(mod?.to ?? '/painel')}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Começar a usar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
